import type { Fetcher } from "../fetcher";
import type { ApiSingleResponse, ApiCollectionResponse } from "../types/api";
import type {
  CreateDocumentDto,
  DocumentDownloadTarget,
  DocumentDTO,
  DocumentUploadTarget,
  DocumentUploadUrlDto,
  ListDocumentsParams,
} from "./types";

export function createDocumentsRepository(fetcher: Fetcher) {
  return {
    async list(params: ListDocumentsParams): Promise<{
      items: DocumentDTO[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }> {
      const qs = new URLSearchParams();
      qs.append("entityType", params.entityType);
      qs.append("entityId", params.entityId);
      if (params.kind) qs.append("kind", params.kind);
      if (params.page) qs.append("page", String(params.page));
      if (params.limit) qs.append("limit", String(params.limit));

      const res = await fetcher<ApiCollectionResponse<DocumentDTO>>(
        `/documents?${qs.toString()}`,
      );
      const payload =
        res.data ??
        ({} as Partial<{
          items: DocumentDTO[];
          pagination: { page: number; limit: number; total: number; totalPages: number };
        }>);
      const pagination = payload.pagination ?? {};
      return {
        items: payload.items ?? [],
        pagination: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 50,
          total: pagination.total ?? 0,
          totalPages: pagination.totalPages ?? 1,
        },
      };
    },

    /**
     * Step 1 of an upload. The server picks the key — clients never propose
     * one — and returns a short-lived presigned PUT.
     */
    async requestUploadUrl(
      payload: DocumentUploadUrlDto,
    ): Promise<DocumentUploadTarget> {
      const res = await fetcher<ApiSingleResponse<DocumentUploadTarget>>(
        "/documents/upload-url",
        { method: "POST", body: JSON.stringify(payload) },
      );
      return res.data;
    },

    /**
     * Step 2. Goes straight to the object store, not through the API, so it
     * deliberately uses bare `fetch` — the API fetcher would attach our auth
     * header, which the presigned URL neither needs nor accepts.
     */
    async uploadFile(
      target: DocumentUploadTarget,
      file: File | Blob,
      contentType?: string,
    ): Promise<void> {
      const res = await fetch(target.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type":
            contentType ||
            (file instanceof File ? file.type : "") ||
            "application/octet-stream",
        },
        body: file,
      });
      if (!res.ok) {
        throw new Error(`Upload failed (${res.status})`);
      }
    },

    async downloadUrl(
      id: string,
      disposition?: "attachment" | "inline",
    ): Promise<DocumentDownloadTarget> {
      const qs = disposition ? `?disposition=${disposition}` : "";
      const res = await fetcher<ApiSingleResponse<DocumentDownloadTarget>>(
        `/documents/${id}/download${qs}`,
      );
      return res.data;
    },

    async create(payload: CreateDocumentDto): Promise<DocumentDTO> {
      const res = await fetcher<ApiSingleResponse<DocumentDTO>>(
        "/documents",
        { method: "POST", body: JSON.stringify(payload) },
      );
      return res.data;
    },

    async delete(id: string): Promise<void> {
      await fetcher(`/documents/${id}`, { method: "DELETE" });
    },
  };
}
